import React, { useEffect, useState } from 'react';
import { Box, Button, useTheme } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useDispatch } from 'react-redux';
import { openDialog } from '@/features/dialog/dialogSlice';
import { useFeatureFlagContext } from '@/context/featureFlagContext';
import { keyframes } from '@emotion/react';

const shineSweep = keyframes`
  from {
    transform: translateX(-150%) skewX(-20deg);
  }
  to {
    transform: translateX(250%) skewX(-20deg);
  }
`;

export const AskAiButton = ({ sx = {}, minimal = false }) => {
	const dispatch = useDispatch();
	const theme = useTheme();

	const [isFlashing, setIsFlashing] = useState(false);

	const { getFeatureFlags } = useFeatureFlagContext();
	const useAskAiFeature = Boolean(getFeatureFlags('useAskAi')?.active);

	useEffect(() => {
		if (!useAskAiFeature) return;
		const timer = setTimeout(() => setIsFlashing(true), 5000);
		return () => clearTimeout(timer);
	}, [useAskAiFeature]);

	const handleFlashEnd = () => {
		setIsFlashing(false);
	};

	return useAskAiFeature ? (
		<Button
			variant='contained'
			color='secondary'
			size={'medium'}
			startIcon={<AutoAwesomeIcon />}
			sx={{
				width: minimal ? 'auto' : '100%',
				minWidth: minimal ? 'auto' : undefined,
				whiteSpace: minimal ? 'nowrap' : undefined,
				borderRadius: '10px',
				border:
					theme.palette.mode === 'dark'
						? `1px solid ${theme.palette.folioPurple}`
						: '1px solid transparent',
				color: 'white',
				fontWeight: 400,
				fontSize: minimal ? '0.8125rem' : undefined,
				px: minimal ? 2 : undefined,
				backgroundColor: theme.palette.folioPurple,
				backgroundImage: `linear-gradient(
      to top right,
      ${theme.palette.folioPurpleAccent} 0%,
      ${theme.palette.primary.main} 50%,
      ${theme.palette.folioPurple} 100%
    )`,
				backgroundSize: '200% 200%',
				backgroundPosition: '0% 100%',
				boxShadow: `0 2px 8px -2px ${theme.palette.folioPurple}40`,
				transform: 'translateY(0) scale(1)',
				position: 'relative',
				overflow: 'hidden',
				'&:hover': {
					backgroundColor: 'white',
					backgroundImage: 'none',
					borderColor: theme.palette.folioPurple,
					color: theme.palette.folioPurple,
					fontWeight: 700,
					transform: 'translateY(-1px) scale(1.01)',
					boxShadow: `0 12px 28px -6px ${theme.palette.folioPurple}40, 0 6px 12px -4px ${theme.palette.folioPurple}30`,
				},
				transition: [
					theme.transitions.create(
						[
							'background-color',
							'background-image',
							'border-color',
							'color',
							'box-shadow',
						],
						{
							duration: theme.transitions.duration.standard,
							easing: theme.transitions.easing.easeInOut,
						},
					),
					theme.transitions.create(['transform'], {
						duration: theme.transitions.duration.shorter,
						easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
					}),
				].join(', '),
				...sx,
			}}
			onClick={() => dispatch(openDialog('askAi'))}
		>
			{isFlashing && (
				<Box
					aria-hidden
					onAnimationEnd={handleFlashEnd}
					sx={{
						position: 'absolute',
						inset: 0,
						width: '45%',
						background: `linear-gradient(
          90deg,
          transparent 0%,
          rgba(255, 255, 255, 0) 25%,
          rgba(255, 255, 255, 0.55) 50%,
          rgba(255, 255, 255, 0) 75%,
          transparent 100%
        )`,
						pointerEvents: 'none',
						animation: `${shineSweep} 1.2s ease-in-out forwards`,
					}}
				/>
			)}
			Ask Folio AI
		</Button>
	) : (
		<></>
	);
};

export default AskAiButton;
